import { useMutation, useQueryClient } from '@tanstack/react-query';

import { removePreference, setV2Preference } from 'api/jalapeno';
import useAccountQuery from 'api/queries/useAccountQuery';
import usePreferencesQuery, { PREFERENCES_QUERY_KEY } from 'api/queries/usePreferencesQuery';
import logError from 'utils/errorUtils';

/**
 * Enum for mutation operations.
 * @readonly
 * @enum {string}
 */
const OperationTypes = Object.freeze({
    REMOVE: 'remove',
    ADD_LIKE: 'add_like',
    ADD_DISLIKE: 'add_dislike',
    ADD_RECURRING: 'add_recurring',
});

/**
 * Optimistic updates for mutating a preferences.
 * @param {Object} [params] - Hook parameters.
 * @param {string} [params.preferenceID] - The default ID of the preference to mutate. Can be overridden per-call.
 * @returns {Object} - Functions for mutating a preference's quantities.
 */
function useUpdatePreference({ preferenceID: defaultPreferenceID } = {}) {
    const queryClient = useQueryClient();
    const { data: account = {} } = useAccountQuery();
    const { data: preferences = {} } = usePreferencesQuery();

    const { subID: subscriptionID } = account;
    const queryKey = [PREFERENCES_QUERY_KEY, subscriptionID];

    /**
     * Build an updated list of preferences, including the mutated preference.
     *   - Can add or remove preferences from likes, dislikes and recurring lists
     * @param {OperationTypes} operation - The operation to perform on the preference.
     * @param {string} preferenceID - The ID of the preference to mutate.
     */
    const updatePreferences = ({ operation, preferenceID }) => {
        const allItems = [];
        const { packPreferences = {}, recurring = [] } = preferences;
        const removePreferenceItem = (array) => array.filter((item) => item.id !== preferenceID);

        const updatedPackPreferences = packPreferences?.map((pack) => {
            const packPreference = pack;
            const { dislikes, favorites, neutral } = pack || {};
            const itemToAdd = neutral.filter((item) => item.id === preferenceID)[0];

            if (operation === 'remove') {
                packPreference.dislikes = removePreferenceItem(packPreference.dislikes);
                packPreference.favorites = removePreferenceItem(packPreference.favorites);
                packPreference.neutral = neutral;
            } else {
                allItems.push(...neutral);
                if (itemToAdd) {
                    if (operation === 'add_dislike') packPreference.dislikes = [...dislikes, itemToAdd];
                    if (operation === 'add_like') packPreference.favorites = [...favorites, itemToAdd];
                }
                packPreference.neutral = removePreferenceItem(neutral);
            }

            return packPreference;
        });

        let updatedRecurring = recurring;

        if (operation === 'remove') {
            if (recurring[0]?.recurring) {
                updatedRecurring = [
                    {
                        ...recurring[0],
                        recurring: removePreferenceItem(recurring[0].recurring),
                    },
                ];
            }
        } else if (operation === 'add_recurring') {
            // Try to find the full item from packPreferences, or create a minimal object
            const itemToAdd = allItems.filter((item) => item.id === preferenceID)[0] || { id: preferenceID };
            if (recurring[0]?.recurring) {
                updatedRecurring = [
                    {
                        ...recurring[0],
                        recurring: [...recurring[0].recurring, itemToAdd],
                    },
                ];
            }
        }

        return {
            packPreferences: updatedPackPreferences,
            recurring: updatedRecurring,
        };
    };

    /**
     * Determines the appropriate API endpoint to call based on the passed in operation.
     * @param {OperationTypes} operation - The operation to perform on the preference's qty.
     * @param {string} preferenceID - The ID of the preference to mutate.
     */
    const getMutationFn = async ({ operation, preferenceID }) => {
        if (operation === OperationTypes.REMOVE) {
            return removePreference(subscriptionID, { itemID: preferenceID });
        }

        const preferenceType = operation.split('_')[1].toUpperCase();
        return setV2Preference(subscriptionID, { itemID: preferenceID, preferenceType });
    };

    /**
     * TanStack Query hook to optimistically update the cache directly.
     * Reference: https://tanstack.com/query/v4/docs/react/guides/optimistic-updates
     */
    const preferenceMutation = useMutation({
        // API call:
        mutationFn: getMutationFn,

        // When mutate is called:
        onMutate: async (args) => {
            // Cancel any outgoing refetches, such that
            // they don't overwrite our optimistic update.
            await queryClient.cancelQueries({ queryKey });

            // Snapshot the previous preferences.
            const previousPreferences = queryClient.getQueryData(queryKey);

            // _Optimistically_ update the preferences.
            queryClient.setQueryData(queryKey, () =>
                updatePreferences({ operation: args.operation, preferenceID: args.preferenceID })
            );

            // Return a context object with the snapshot of previous preferences.
            return { previousPreferences };
        },

        // If the mutation fails:
        onError: (err, _variables, context) => {
            // Use the context returned from onMutate to roll back.
            if (context) queryClient.setQueryData(queryKey, context.previousPreferences);
            logError(err);
            console.error(err);
        },

        // After error or success:
        onSettled: () => {
            // Refetch preferences.
            queryClient.invalidateQueries({ queryKey });
        },
    });

    /*
     * mutationOptions
     *
     * https://tanstack.com/query/v4/docs/framework/react/reference/useMutation
     *
     * { onSuccess, onSettled, onError }
     *
     * Each function accepts an optional object with:
     *   - preferenceID: Override the hook's default preferenceID for this call
     *   - ...mutationOptions: TanStack Query mutation options (onSuccess, onError, onSettled, etc.)
     */

    // Removes preference from likes/dislikes list
    const removeUserPreference = (options) => {
        const { preferenceID = defaultPreferenceID, ...mutationOptions } = options ?? {};
        preferenceMutation.mutate({ subscriptionID, operation: OperationTypes.REMOVE, preferenceID }, mutationOptions);
    };

    // Adds preference to liked list
    const addLikedUserPreference = (options) => {
        const { preferenceID = defaultPreferenceID, ...mutationOptions } = options ?? {};
        preferenceMutation.mutate(
            { subscriptionID, operation: OperationTypes.ADD_LIKE, preferenceID },
            mutationOptions
        );
    };

    // Adds preference to disliked list
    const addDislikedUserPreference = (options) => {
        const { preferenceID = defaultPreferenceID, ...mutationOptions } = options ?? {};
        preferenceMutation.mutate(
            { subscriptionID, operation: OperationTypes.ADD_DISLIKE, preferenceID },
            mutationOptions
        );
    };

    // Adds preference to recurring list
    const addRecurringUserPreference = (options) => {
        const { preferenceID = defaultPreferenceID, ...mutationOptions } = options ?? {};
        preferenceMutation.mutate(
            { subscriptionID, operation: OperationTypes.ADD_RECURRING, preferenceID },
            mutationOptions
        );
    };

    return {
        removeUserPreference,
        addLikedUserPreference,
        addDislikedUserPreference,
        addRecurringUserPreference,
    };
}

export default useUpdatePreference;
